'use strict';

import Utils from 'components/utils';
import template from 'components/template';

// IE/Edge Hack
//After Edge 16 the issue is not reproduced
const ieHack = (() => document.body.style.msTouchAction !== undefined || navigator.userAgent.search(/Edge\/1[0-5]/) > 0)();
const pageTitle = document.title;
const selectTimingErrorToggle = document.documentElement.dataset.selectTiming;
const profileDateValidationFixToggle = document.documentElement.dataset.profileDateValidationFix;
const expirationDateErrorMessageSpacingFixToggle = document.documentElement.dataset.expirationDateErrorMessageSpacingFix;
const TWO_INPUTS_DATEPICKER_SELECTOR = '.two-inputs-datepicker';
const INPUT_COMPOSITE_SELECTOR = '.input-composite';
const TIME_FIELD_SELECTOR = '.input-date--time';
const TIME_COMPOSITE_SELECTOR = '.input-composite.input-time';
const TIME_WINDOW_SELECT_SELECTOR = '.time-window-container select';
const TIME_WINDOW_ERROR_CLASS = 'assistive-text--error-time-window';
const INVALID_DATE = 'Invalid date';
const ARIA_INVALID_ATTR = 'aria-invalid';
const ARIA_DESCRIBEDBY_ATTR = 'aria-describedby';
const formFieldConstraintsErrorsAccessibilityToggleEnabled = document.documentElement.dataset.formFieldConstraintsErrorsAccessibility;
const manageDobGuestTravelerEnabled = document.documentElement.dataset.manageDobGuestTravelerEnabled;
// CYT-22645. Toggle removal: remove this constant and every `timeWindowValidationFixToggle` check in this
// file; keep the new code and remove the previous logic as noted at each "CYT-22645" place.
const timeWindowValidationFixToggle = document.documentElement.dataset.airTimeWindowValidationFix;

let errorIdCounter = 1;
let invalidInputsLength = 0;

function getInvalidInputsAndForceSubmit(form, fieldsOnly)
{
    let invalidInputs = form.querySelectorAll((fieldsOnly ? '.field > ' : '') + ':invalid:enabled');

    // IE/Edge Hack
    let forceSubmit = false;
    if (Utils.isEdge() || ieHack) {
        const tmp = [];
        forceSubmit = !!invalidInputs.length;

        // Edge browser considers a not required, empty input with attribute for minlength as invalid in case you enter some value and then delete it
        if (Utils.isEdge()) {
            for (const input of invalidInputs) {
                if (!input.value && !input.required) {
                    continue;
                } else {
                    tmp.push(input);
                }
            }
            invalidInputs = tmp;
        }

        // IE/Edge(to 16 version) consider a required Select2 field as 'valueMissing'
        // even if there's a value (e.g. selectedIndex >= 0).
        if (ieHack) {
            for (const input of invalidInputs) {
                if (!(input.dataset.inputSelect && input.selectedIndex >= 0 && input.validity.valueMissing)) {
                    tmp.push(input);
                }
            }
            invalidInputs = tmp;
        }

        forceSubmit &= !invalidInputs.length;
    }

    return {invalidInputs, forceSubmit};
}

// CYT-22645: replace invalid fieldsets by their invalid control, so the error count matches the links
function resolveInvalidControl(element)
{
    if (!element || (element.validity && !element.validity.valid && element.nodeName !== 'FIELDSET')) {
        return element;
    }

    for (const control of element.querySelectorAll('input, select, textarea')) {
        if (!control.disabled && control.validity && !control.validity.valid) {
            return control;
        }
    }

    return null;
}

function normalizeInvalidInputs(invalidInputs)
{
    const result = [];

    for (const element of invalidInputs) {
        if (!isInTimeWindowField(element)) {
            result.push(element);
            continue;
        }
        const control = resolveInvalidControl(element);
        if (control && result.indexOf(control) < 0) {
            result.push(control);
        }
    }

    return result;
}

function isTimeWindowSelect(element)
{
    return !!(element && element.matches && element.matches(TIME_WINDOW_SELECT_SELECTOR));
}

// CYT-22645: element belongs to a field with a Time Window (other fields keep the previous behaviour)
function isInTimeWindowField(element)
{
    const field = element && element.closest && element.closest('.field');
    return !!(field && field.querySelector(TIME_WINDOW_SELECT_SELECTOR));
}

// CYT-22645: Time composite of the given Time Window select, or null
function getTimeCompositeForTimeWindow(select)
{
    const timeField = select.closest(TIME_FIELD_SELECTOR);
    if (!timeField) {
        return null;
    }

    for (const child of timeField.children) {
        if (child.matches(TIME_COMPOSITE_SELECTOR)) {
            return child;
        }
    }

    return timeField.querySelector(TIME_COMPOSITE_SELECTOR);
}

// CYT-22645: focus the element once it is visible (panel opening transition)
function focusWhenVisible(element, timeout = 500)
{
    const start = performance.now();
    const tryFocus = () => {
        if (getComputedStyle(element).visibility === 'visible' && element.getClientRects().length) {
            element.focus();
            return;
        }
        if (performance.now() - start < timeout) {
            requestAnimationFrame(tryFocus);
        } else {
            element.focus();
        }
    };
    tryFocus();
}

// CYT-22645: field enclosing the Time field (holds the Time Window caption), or null
function getOuterFieldOfTimeField(element)
{
    const timeField = element && element.closest(TIME_FIELD_SELECTOR);
    return timeField && timeField.parentElement
        ? timeField.parentElement.closest('.field')
        : null;
}

function removeErrorText(input)
{
    let field = input.closest('.field'),
        datePickerField;

    /**
     *  BUGFIX TOGGLE
     *  CYT-19373 [A11Y-23] No error message is getting displayed on entering any invalid input in Select timing for your booking (Minute) input field
     */
    if (selectTimingErrorToggle) {
         datePickerField = field.closest(TWO_INPUTS_DATEPICKER_SELECTOR) || input.closest('.hotel-start-dates') || input.closest('.car-start-dates');
    } else {
         datePickerField = field.closest(TWO_INPUTS_DATEPICKER_SELECTOR);
    }

    // CYT-22645 (toggle removal: drop `!!timeWindowValidationFixToggle &&`)
    const isTimeError = !!timeWindowValidationFixToggle
        && (isTimeWindowSelect(input)
            || (input.matches && input.matches(TIME_COMPOSITE_SELECTOR) && isInTimeWindowField(input)));

    // In case of error in datepicker
    if (datePickerField) {
        field = datePickerField;

        /**
         *  BUGFIX TOGGLE
         *  CYT-19373 [A11Y-23] No error message is getting displayed on entering any invalid input in Select timing for your booking (Minute) input field
         */
        if (selectTimingErrorToggle) {
            // CYT-22645: not for Time errors (keeps the Date error).
            // Keep this check when removing the CYT-19373 toggle; it also stays on CYT-22645 toggle removal.
            if (!isTimeError) {
                datePickerField.querySelector(INPUT_COMPOSITE_SELECTOR) && datePickerField.querySelector(INPUT_COMPOSITE_SELECTOR).classList.remove('error');
            }
        }
    } else if (isTimeError) {
        // CYT-22645: Time Window caption is in the enclosing field
        field = getOuterFieldOfTimeField(input) || field;
    }

    if (field) {
        // CYT-22645: remove only the caption of this input (Date or Time Window)
        // Toggle removal: keep the if-body, remove the else part (previous logic).
        if (timeWindowValidationFixToggle) {
            let errorText;
            if (isTimeError) {
                errorText = field.querySelector(`:scope > .${TIME_WINDOW_ERROR_CLASS}`);
            } else {
                errorText = field.lastElementChild;
                if (errorText && errorText.classList.contains(TIME_WINDOW_ERROR_CLASS)) {
                    errorText = errorText.previousElementSibling;
                }
            }
            if (errorText && errorText.classList.contains('assistive-text--error')) {
                field.removeChild(errorText);
            }
        } else {
            const errorText = field.lastElementChild;
            if (errorText.classList.contains('assistive-text--error')) {
                field.removeChild(errorText);
            }
        }
        input.classList.remove('error');
        input.setAttribute(ARIA_INVALID_ATTR, 'false');

        // CYT-22645: clear Time composite and Time Window select together
        // Toggle removal: drop only the if-check, keep its body.
        if (timeWindowValidationFixToggle) {
            if (isTimeWindowSelect(input)) {
                const timeComposite = getTimeCompositeForTimeWindow(input);
                timeComposite && timeComposite.classList.remove('error');
            } else if (input.matches && input.matches(TIME_COMPOSITE_SELECTOR)) {
                const timeField = input.closest(TIME_FIELD_SELECTOR),
                      timeWindowSelect = timeField && timeField.querySelector(TIME_WINDOW_SELECT_SELECTOR);
                timeWindowSelect && timeWindowSelect.classList.remove('error');
            }
        }

        /**
         *  BUGFIX TOGGLE
         *  CYT-19853 [A11Y-176] Ensure form field constraints and errors are associated with their corresponding field
         */
        if (formFieldConstraintsErrorsAccessibilityToggleEnabled) {
            // Clean up aria attributes from all the necessary inputs
            const childEls = field.querySelectorAll('.input-composite, input, select, textarea, fieldset');
            for (const child of childEls) {
                child.removeAttribute(ARIA_INVALID_ATTR);
                child.removeAttribute(ARIA_DESCRIBEDBY_ATTR);
            }

            // Clean up aria attributes from select2 fields
            const selectErrorField = field.querySelector('.select2-selection.select2-selection--single');
            if (selectErrorField) {
                selectErrorField.removeAttribute(ARIA_INVALID_ATTR);
                selectErrorField.removeAttribute(ARIA_DESCRIBEDBY_ATTR);
            }
        }
    }

    removeErrorTextFromComponent(input);
}

function removeErrorTextFromComponent(input)
{
    let errorWrapper = getErrorComponent();

    if (errorWrapper && input.name) {
        let inputError = errorWrapper.querySelector(`a[data-name=${input.name}]`);
        if (inputError) {
            inputError.closest('li').remove();

            let errorNumber = errorWrapper.querySelector('.error-number'),
                errorNumberInt = parseInt(errorNumber.innerHTML);
            errorNumberInt--;
            errorNumber.innerHTML = errorNumberInt;

            invalidInputsLength--;
            addErrorTitle();

            if (invalidInputsLength == 0) {
                destroyErrorComponent();
            }
        }
    }
}

function getErrorComponent()
{
    return document.querySelector('.error-wrapper');
}

function createErrorComponent(form)
{
    const errorNotifications = template.render('error-notifications', {errors: invalidInputsLength}),
          header = document.querySelector('.workflow-page-header'),
          errorWithinForm = form.dataset.errorWrapperWithinForm,
          errorWrapperTarget = form.dataset.errorWrapperTarget;

    /**
     *  BUGFIX TOGGLE
     *  CYT-19857 Number of errors message is not shown on the correct place
     */
    const errorWithinFormEnabled = document.documentElement.dataset.errorWrapperWithinForm;

    // Support for custom error target (e.g., inside a modal)
    if (errorWrapperTarget) {
        const targetElement = form.querySelector(errorWrapperTarget);
        if (targetElement) {
            targetElement.insertAdjacentHTML('afterbegin', errorNotifications);
            return;
        }
    }

    if (errorWithinFormEnabled) {
        if (errorWithinForm) {
            form.insertAdjacentHTML('afterbegin', errorNotifications);
        } else if(header) {
            header.insertAdjacentHTML('afterend', errorNotifications);
        } else {
            form.insertAdjacentHTML('afterbegin', errorNotifications);
        }
    } else {
        if (header) {
            header.insertAdjacentHTML('afterend', errorNotifications);
        } else {
            form.insertAdjacentHTML('afterbegin', errorNotifications);
        }
    }
}

function destroyErrorComponent()
{
    let errorWrapper = getErrorComponent();

    if (errorWrapper) {
        errorWrapper.remove();
        document.title = pageTitle;
    }
}

function addErrorTitle()
{
    let errorsTitle = template.render('error-page-title', {invalidInputsLength: invalidInputsLength, pageTitle: pageTitle});
    if (errorsTitle) {
        document.title = errorsTitle;
    }
}

// Resolves the visible label text for a field, tolerating composite fields whose
// wrapper has no label-bearing next sibling. Returns '' when nothing is resolvable.
function resolveLabelText(inputEl, fieldLabelEl)
{
    const sibling = inputEl && inputEl.nextSibling;
    const fromSibling = sibling && sibling.firstChild ? sibling.firstChild.innerHTML.trim() : null;

    if (fromSibling) {
        return fromSibling;
    }

    if (fieldLabelEl) {
        return (fieldLabelEl.firstChild && fieldLabelEl.firstChild.innerHTML.trim())
            || fieldLabelEl.textContent.trim();
    }

    return '';
}

function addErrorText(input)
{
    let input1 = input,
        form = input.closest('form'),
        field = input.closest('.field'), // The value of the input is still needed in case of change because of the input-composite
        // CYT-22645: use the composite wrapping the input (field may contain Date and Time composites)
        // Toggle removal: drop `timeWindowValidationFixToggle &&`, keep the rest.
        inputComposite = (timeWindowValidationFixToggle && isInTimeWindowField(input) && input.closest(INPUT_COMPOSITE_SELECTOR))
            || field.querySelector(INPUT_COMPOSITE_SELECTOR);

    // CYT-22645: Time Window select -> mark the Time composite and the select
    // Toggle removal: drop `timeWindowValidationFixToggle &&`, keep the rest.
    if (timeWindowValidationFixToggle && isTimeWindowSelect(input)) {
        const timeComposite = getTimeCompositeForTimeWindow(input);
        if (timeComposite) {
            inputComposite = timeComposite;
            field = input.closest(TIME_FIELD_SELECTOR) || field;
        }
        input.classList.add('error');
        input.setAttribute(ARIA_INVALID_ATTR, 'true');
    }
    if (inputComposite) {
        input = inputComposite;
    }

    input.classList.add('error');
    input.setAttribute(ARIA_INVALID_ATTR, 'true');

    let templateText,
        message,
        customErrorText,
        validity,
        inputField = input.closest('.field'),
        fieldLabel = inputField ? inputField.querySelector('.field-label') : null,
        existingLabel = fieldLabel ? true : false;

    const radioLabel = inputField ? inputField.querySelector('.radio-label') : null;
    let existingRadioLabel = radioLabel ? true : false,
        invalidControl = input;


    if (inputComposite) {
        // CYT-22645: search the whole field (Time Window select is outside the composite)
        // Toggle removal: drop `timeWindowValidationFixToggle &&`, keep the rest.
        const invalidInputs = (timeWindowValidationFixToggle && isTimeWindowSelect(input1) ? field : input).querySelectorAll(':invalid');
        for (let invalidInput of invalidInputs) {
            if (!invalidInput.validity.valid) {
                invalidInput.classList.add('error');
                validity = invalidInput.validity;
                invalidControl = invalidInput;
                break;
            }
        }
    } else {
        validity = input.validity;
    }

    if (!validity) {
        return;
    }

    // CYT-22645: radio/checkbox message only if the invalid control is a radio/checkbox
    // Toggle removal: drop `!!timeWindowValidationFixToggle &&`, keep the rest.
    const timeWindowField = !!timeWindowValidationFixToggle && isInTimeWindowField(input1);
    if (timeWindowField && existingRadioLabel && invalidControl
        && invalidControl.type !== 'radio' && invalidControl.type !== 'checkbox') {
        existingRadioLabel = false;
    }

    if (validity.valid) {
        // The element meets all constraint validations, and is therefore
        // considered to be valid.
        return;
    } else if (validity.valueMissing) {
        // The element has a required attribute, but no value.
        customErrorText = input.dataset.customErrorText;
        templateText = existingLabel ? 'input-error-required-label' : 'input-error-required';
        if(existingRadioLabel) {
            templateText = 'input-error-required-checkbox';
        }
        message = input.dataset.inputErrorRequired;
    } else if (validity.badInput) {
        // The user has provided input that the browser is unable to convert.
    } else if (validity.customError) {
        //Custom error when the input date is invalid
        if (input.classList.contains('input-date-expiry')) {
            templateText = existingLabel ? 'input-error-date-label' : 'input-error-date';
            message = input.dataset.inputErrorPattern;
        }
    } else if (validity.patternMismatch) {
        // The value does not match the specified pattern.
        templateText = existingLabel ? 'input-error-pattern-label' : 'input-error-pattern';
        message = input.dataset.inputErrorPattern;
    } else if (validity.rangeOverflow) {
        // The value is greater than the maximum specified by the max attribute.
    } else if (validity.rangeUnderflow) {
        // The value is less than the minimum specified by the min attribute.
    } else if (validity.stepMismatch) {
        // The value does not fit the rules determined by the step attribute
        // (that is, it's not evenly divisible by the step value).
    } else if (validity.tooLong) {
        // The value exceeds the specified maxlength for HTMLInputElement or
        // HTMLTextAreaElement objects. Note: This will never be true in Gecko,
        // because elements' values are prevented from being longer than
        // maxlength.
    } else if (validity.tooShort) {
        // Value is shorter than @minlength.
        templateText = existingLabel ? 'input-error-short-label' : 'input-error-short';
        message = input.dataset.inputErrorShort;
    } else if (validity.typeMismatch) {
        // Value is not valid email/url.
        templateText = existingLabel ? `input-error-type-${input.type}-label` : `input-error-type-${input.type}`;
        message = input.dataset['inputError' + input.type.charAt(0).toUpperCase() + input.type.substr(1)];
    }

    if (templateText || message) {

        // In case we do NOT need an error wrapper, error message will appear on the bottom of the input
        if (form.dataset.formPreventErrorWrapper) {
            if (message) {
                field.insertAdjacentHTML('beforeend', `<span class="assistive-text--error text--caption">${message}</span>`);
            } else {
                const text = expirationDateErrorMessageSpacingFixToggle
                    ? resolveLabelText(input, fieldLabel)
                    : input.nextSibling.firstChild.innerHTML;
                if (!text) {
                    templateText = templateText.replace(/-label$/, '');
                }
                let errorText = template.render(templateText, {label: text});
                field.insertAdjacentHTML('beforeend', `<span class="assistive-text--error text--caption">${errorText}</span>`);
            }
            input.focus();
        } else {
            let text,
                content = document.querySelector('.error-wrapper').querySelector('.text-wrapper ul');

            let errorText;

            if (message) {
                errorText = message
            } else if (customErrorText) {
                errorText = customErrorText;
            } else {
                // CYT-22645: radio label first (avoids label HTML in the link text)
                if (timeWindowField && existingRadioLabel) {
                    text = radioLabel.textContent;
                    templateText = template.render(templateText, {label: text});
                    errorText = templateText;
                } else if (existingLabel) {
                    if (!inputComposite) {
                        text = fieldLabel.firstChild.innerHTML;
                    } else {
                        text = expirationDateErrorMessageSpacingFixToggle
                            ? resolveLabelText(input, fieldLabel)
                            : input.nextSibling.firstChild.innerHTML;
                    }
                    if (!text) {
                        templateText = templateText.replace(/-label$/, '');
                    }
                    templateText = template.render(templateText, {label: text});
                    errorText = templateText;
                } else if(existingRadioLabel) {
                    text = radioLabel.textContent;
                    templateText = template.render(templateText, {label: text});
                    errorText = templateText;
                } else {
                    errorText = templateText.content.textContent;
                }
            }

            // In case of error in datepicker
            const datePickerField = inputField.closest(TWO_INPUTS_DATEPICKER_SELECTOR)
            if (datePickerField) {
                inputField = datePickerField;
            } else if (timeWindowValidationFixToggle && isTimeWindowSelect(input1)) {
                // CYT-22645: Time Window caption below the Date & Time field
                // Toggle removal: drop `timeWindowValidationFixToggle &&`, keep the rest.
                inputField = getOuterFieldOfTimeField(input1) || inputField;
            }

            // CYT-22645: separate Time Window caption, so it does not overwrite the Date caption
            // Toggle removal: drop `!!timeWindowValidationFixToggle &&`; in the if/else-if/else below keep the
            // first two branches (without the toggle check) and remove the last else (previous logic).
            const isTimeWindowError = !!timeWindowValidationFixToggle && isTimeWindowSelect(input1),
                  captionClass = isTimeWindowError ? `assistive-text--error text--caption ${TIME_WINDOW_ERROR_CLASS}` : 'assistive-text--error text--caption';
            let assistiveText;
            if (isTimeWindowError) {
                assistiveText = inputField.querySelector(`:scope > .assistive-text--error.${TIME_WINDOW_ERROR_CLASS}`);
            } else if (timeWindowValidationFixToggle) {
                assistiveText = inputField.querySelector(`.assistive-text--error:not(.${TIME_WINDOW_ERROR_CLASS})`);
            } else {
                assistiveText = inputField.querySelector('.assistive-text--error');
            }

            if (!assistiveText) {
                if(!existingRadioLabel) {
                    /**
                     *  BUGFIX TOGGLE
                     *  CYT-19853 [A11Y-176] Ensure form field constraints and errors are associated with their corresponding field
                     */
                    if (formFieldConstraintsErrorsAccessibilityToggleEnabled) {
                        const errorId = `error-${errorIdCounter++}`;
                        inputField.insertAdjacentHTML('beforeend', `<span class="${captionClass}" id="${errorId}">${errorText}</span>`);
                        // No need to add aria-invalid, since it's default behaviour for inputs
                        input.setAttribute(ARIA_DESCRIBEDBY_ATTR, errorId);

                        const selectErrorField = inputField.querySelector('.select2-selection.select2-selection--single');
                        if (selectErrorField) {
                            selectErrorField.setAttribute(ARIA_INVALID_ATTR, 'true');
                            selectErrorField.setAttribute(ARIA_DESCRIBEDBY_ATTR, errorId);
                        }
                    } else {
                        inputField.insertAdjacentHTML('beforeend', `<span class="${captionClass}">${errorText}</span>`);
                    }
                }
            } else {
                /**
                 *  BUGFIX TOGGLE
                 *  CYTTRA-7821 Manage DOB for guest traveler
                 */
                if (manageDobGuestTravelerEnabled) {
                    assistiveText.textContent = errorText;
                } else {
                    assistiveText.insertAdjacentHTML('beforeend', errorText);
                }
            }

            /**
             *  BUGFIX TOGGLE
             *  CYT-19853 [A11Y-176] Ensure form field constraints and errors are associated with their corresponding field
             */
            if (formFieldConstraintsErrorsAccessibilityToggleEnabled) {
                const childName = input1.querySelector('[name]') ? input1.querySelector('[name]').name : '';
                const name = input1.name ? input1.name : childName;
                content.insertAdjacentHTML('beforeend', `<li><a href="#" class="error-link active" data-name='${name}'>${errorText}</a></li>`);
            } else {
                content.insertAdjacentHTML('beforeend', `<li><a href="#" class="error-link active" data-name='${input1.name}'>${errorText}</a></li>`);
            }
        }
    }
}

function validateForm(form)
{
    if (!form) {
        return;
    }

    for (const input of form.querySelectorAll('.field > input, .field > select, .field > textarea, .field > .input-composite')) {
        removeErrorText(input);
    }

    const invalidInputsAndForceSubmit = getInvalidInputsAndForceSubmit(form, true),
          forceSubmit = invalidInputsAndForceSubmit.forceSubmit;
    let invalidInputs = [],
        invalidInputInModal = false;

    for (let input of invalidInputsAndForceSubmit.invalidInputs) {
        invalidInputs.push(input);
    }

    // Get only the first error of input composite
    let inputComposite = form.querySelectorAll('.field > .input-composite');
    for (let input of inputComposite) {
        // CYT-22645: search the whole field (Time Window select is outside the composite)
        // Toggle removal: drop `timeWindowValidationFixToggle &&`, keep the rest.
        const invalid = (timeWindowValidationFixToggle && isInTimeWindowField(input) ? input.closest('.field') : input).querySelector(':invalid');
        if (invalid) {

            /**
             * BUGFIX TOGGLE
             * CYT-19373 [A11Y-23] No error message is getting displayed on entering any invalid input in Select timing for your booking (Minute) input field
             */
            if (selectTimingErrorToggle) {
                const duplicateInvalid = invalidInputs.find(item => item === invalid);
                !duplicateInvalid && invalidInputs.push(invalid);
            } else {
                invalidInputs.push(invalid);
            }
        }
    }

    // CYT-22645: count only linkable controls
    // Toggle removal: drop only the if-check, keep its body.
    if (timeWindowValidationFixToggle) {
        invalidInputs = normalizeInvalidInputs(invalidInputs);
    }

    // Check if form validation is not needed on the page, but somewhere else(i.e. in a modal-dialog)
    if (!form.dataset.formPreventErrorWrapper) {
        let errorWrapper = getErrorComponent();

        if (invalidInputs.length > 0) {
            invalidInputsLength = invalidInputs.length;
            // Ensure that there will not be more than one error component
            if (errorWrapper) {
                destroyErrorComponent();
            }
            createErrorComponent(form);
        } else {
            destroyErrorComponent();
        }
    }

    for (const input of invalidInputs) {
        try {
            addErrorText(input);
        } catch (e) {
            continue;
        }
        // Check if the error is in modal dialog and open it
        let modal = input.closest('.modal-dialog-container');

        //Start: ACT-1607: Added to open the Time window in start Page
        let inputTimePanel = input.closest('.input-select-time-panel');
        if(inputTimePanel && !invalidInputInModal) {
            let parent = inputTimePanel.parentElement;
            parent.dispatchEvent(new CustomEvent('click', {bubbles: true}));
            focus = parent;
            invalidInputInModal = true;
        }
        //End: ACT-1607

        if (modal && !invalidInputInModal) {
            openModal(modal);
            invalidInputInModal = true;
        }
    }

    let erorrLinks = document.querySelectorAll('.error-link');

    for (let link of erorrLinks) {
        link.addEventListener('click', (e) => {
            e.preventDefault();
            let field = document.querySelector("[name='" + link.dataset.name  + "']");
            if (field) {
                // CYT-22645: open the time panel before focusing a control inside it
                // Toggle removal: drop `timeWindowValidationFixToggle &&`, keep the rest.
                const timePanel = timeWindowValidationFixToggle && isInTimeWindowField(field) ? field.closest('.input-select-time-panel') : null;
                if (timePanel) {
                    if (!timePanel.classList.contains('open')) {
                        const trigger = timePanel.parentElement;
                        trigger && trigger.dispatchEvent(new CustomEvent('click', {bubbles: true}));
                    }
                    focusWhenVisible(field);
                } else {
                    field.focus();
                }
            }
        });
    }

    // todo GT - show the input if it is (in a) hidden (section)
    // todo GT - smooth scroll to the input
    requestAnimationFrame(() => {
        let firstError = erorrLinks[0];
        if (firstError) {
            firstError.focus();
        }
    });

    if (invalidInputsLength > 0) {
        addErrorTitle();
    } else {
        document.title = pageTitle;
    }

    // IE/Edge Hack
    if (forceSubmit && !invalidInputs.length) {
        form.submit();
    }
}

function openModal(modal) {
    let lightBoxToggler = modal.previousSibling.previousSibling;

    if (lightBoxToggler) {
        lightBoxToggler.checked = true;
        lightBoxToggler.dispatchEvent(new CustomEvent('click', {bubbles: true}));
    }
}

function validateDate(inputGroup)
{
    let validDates = true;

    let day, month, year, min, max, firstInputInDateGroup, dayFieldIsPresent, expiryDayField, isYearRequired, expiryYearField;
    for (let date of inputGroup) {
        firstInputInDateGroup = date.querySelector('input');
        firstInputInDateGroup.setCustomValidity('');
        dayFieldIsPresent = true;
        expiryDayField = false;
        expiryYearField = false;
        isYearRequired = true;

        day = date.querySelector('input[class*="input-date-expiry-day"]');
        if (day && day.name && day.name.startsWith('foid')) {
          expiryDayField = true;
        }
        if (day !== null){
            if (day.validity.valid){
                day = day.value;
            } else {
                firstInputInDateGroup.setCustomValidity('Invalid day');
                validDates = false;
                continue;
            }
        } else {
            // if the day field is missing it means the date is mm/yyyy only,
            // so we take today's day
            day = new Date().getDate().toString();
            dayFieldIsPresent = false;
        }
        month = date.querySelector('input[class*="input-date-expiry-month"]');
        if (month.validity.valid){
            month = month.value;
        } else {
            firstInputInDateGroup.setCustomValidity('Invalid month');
            validDates = false;
            continue;
        }
        year = date.querySelector('input[class*="input-date-expiry-year"]');
        if (profileDateValidationFixToggle && year && year.name && year.name.startsWith('foid')) {
            expiryYearField = true;
        }
        isYearRequired = year && year.required;
        if (year.validity.valid){
            year = year.value;
        } else {
            firstInputInDateGroup.setCustomValidity('Invalid year');
            validDates = false;
            continue;
        }

        // if the date is only partially entered
        if (!(profileDateValidationFixToggle ? expiryYearField : expiryDayField)) {
            if ((day && dayFieldIsPresent) || month || year) {
                if (!((day || !dayFieldIsPresent) && month && year)) {
                    firstInputInDateGroup.setCustomValidity('Incomplete date');
                    validDates = false;
                    continue;
                }
            } else {
                continue;
            }
        }

        // if we get to this point, it means the individual fields
        // for day, month and year are all filled and haven't failed
        // any other single input constraints

        // First, check whether the day is in the margin of the specific month
        let monthLength = [ 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31 ];

        // Adjust for leap years
        if (year % 400 == 0 || (year % 100 != 0 && year % 4 == 0)) {
            monthLength[1] = 29;
        }

        // Check the range of the day
        if (!((profileDateValidationFixToggle ? expiryYearField : expiryDayField) || (day > 0 && (!dayFieldIsPresent || day <= monthLength[month - 1])))) {
            firstInputInDateGroup.setCustomValidity('Invalid day');
            validDates = false;
            continue;
        }


        // Second, check whether the whole date is within the defined min-max margin
        // adjust the day and month according to the min and max format
        if (day.length == 1) day = '0' + day;
        if (month.length == 1) month = '0' + month;

        min = date.getAttribute("min");
        max = date.getAttribute("max");

        let datez = year + "-" + month + "-" + day;
        if (profileDateValidationFixToggle ? expiryYearField : (expiryDayField && !isYearRequired)) {
            if ((day && (!month || !year)) || (month && !year)) {
                // Check for incomplete date fields
                firstInputInDateGroup.setCustomValidity(INVALID_DATE);
                validDates = false;
            } else if (month === '00' || day === '00') {
                // Check for invalid month or day
                firstInputInDateGroup.setCustomValidity(INVALID_DATE);
                validDates = false;
            } else if (min && max) {
                const minYear = min.split('-')[0];
                const maxYear = max.split('-')[0];
                // Check year range
                if (year && (year < minYear || year > maxYear)) {
                    firstInputInDateGroup.setCustomValidity(INVALID_DATE);
                    validDates = false;
                }
            }
        } else if (datez < min || datez > max) {
            firstInputInDateGroup.setCustomValidity(INVALID_DATE);
            validDates = false;
        }
    }

    return validDates;
}

export default {
    getInvalidInputsAndForceSubmit,
    removeErrorText,
    addErrorText,
    validateForm,
    validateDate
};
